import useClickOutside from "@mirage/hooks/useClickOutside";
import { useRef, useState, useCallback, memo } from "react";
import { HexColorPicker, RgbaColorPicker, RgbaColor } from "react-colorful";
import "./PopoverColorPicker.css";
import { ModeTypes } from "../mirageGlobalButtons/MirageRecordActionButtons";

export type PopoverColorPickerProps = PopoverColorPickerRgbaProps | PopoverColorPickerHexProps;
type PopoverColorPickerRgbaProps = {
    color?: RgbaString | null;
    onChange: (color: RgbaString) => void;
    rgba: true;
    mode: ModeTypes;
};
type PopoverColorPickerHexProps = {
    color?: string | null;
    onChange: (color: string) => void;
    rgba?: false;
    mode: ModeTypes;
};
export type RgbaString = `rgba(${number}, ${number}, ${number}, ${number})`;

export const PopoverColorPicker = memo((props: PopoverColorPickerProps) => {
    const { color, onChange, mode, rgba } = props;
    const popover = useRef<HTMLDivElement>(null);
    const [isOpen, toggle] = useState(false);
    const close = useCallback(() => toggle(false), []);
    useClickOutside(popover, close);
    const className = "swatch " + (mode === "edit" ? "swatch--pointer" : "");
    const rgbaToString = (color: RgbaColor): RgbaString => {
        return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
    };
    const rgbaStringToRgbaColor = (rgbaString: RgbaString): RgbaColor => {
        const rgbaColor = {
            r: parseInt(rgbaString.match(/rgba\((\d+),/)?.[1] ?? "0"),
            g: parseInt(rgbaString.match(/,\s*(\d+),/)?.[1] ?? "0"),
            b: parseInt(rgbaString.match(/,\s*(\d+),\s*[\d.]+\)/)?.[1] ?? "0"),
            a: parseFloat(rgbaString.match(/,\s*([\d.]+)\)/)?.[1] ?? "1"),
        };
        return rgbaColor;
    };
    const handleChange = (color: RgbaColor | string) => {
        if (rgba) {
            onChange(rgbaToString(color as RgbaColor));
        } else {
            onChange(color as string);
        }
    };

    const getBackgroundColor = (color: string | RgbaColor | null | undefined): string => {
        if (!color) {
            return "#fff";
        }
        if (typeof color === "string") {
            return color;
        }
        return rgbaToString(color);
    };

    const renderColorPicker = () => {
        if (rgba) {
            return <RgbaColorPicker color={rgbaStringToRgbaColor(color as RgbaString)} onChange={handleChange} />;
        }
        return <HexColorPicker color={(color as string) ?? "#fff"} onChange={handleChange} />;
    };

    return (
        <div className="picker">
            <div
                className={className}
                style={{ backgroundColor: getBackgroundColor(color) }}
                onClick={() => {
                    if (mode === "edit") {
                        toggle(true);
                    }
                }}
            />
            {isOpen && (
                <div className="popover" ref={popover}>
                    {renderColorPicker()}
                </div>
            )}
        </div>
    );
});
